import { checkSession } from "@features/auth";
import { getUserOrganizationId } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@shadcn/ui/alert-dialog";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { ArrowLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { z } from "zod";
import { formatConfigurationLabel, formatDimensions, getSizeBucket, type SizeBucket } from "~/components/lock/spaceConfiguration";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { SizeTabs } from "~/components/warenannahme/SizeTabs";
import { CollectedSummary } from "~/components/warenannahme/CollectedSummary";
import { useHubGate } from "~/components/warenannahme/useHubGate";
import { compartmentSide, WarenannahmeDoorFlow } from "~/components/warenannahme/WarenannahmeDoorFlow";
import { warenannahmeItems, type WarenannahmeItem } from "~/components/warenannahme/warenannahmeItems";
import type { pickupWarenannahmeQuery } from "./__generated__/pickupWarenannahmeQuery.graphql";

export const query = graphql`
	query pickupWarenannahmeQuery($hubName: String!, $skipOwnedReservations: Boolean!) @relay_test_operation {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			street
			city
			postalCode
			lat
			lng
			organization {
				id
			}
			hub @required(action: THROW) {
				id
				status
				spaces {
					frame
				}
			}
		}
		myUtilizationGrants(input: {}, pagination: { page: 0, pageSize: 1000 }) {
			nodes {
				id
				deactivatedAt
				permissions
				reservation {
					id
					startedAt
					deliveredAt
					deactivatedAt
					contentNote
					note
					flow
					subtype {
						__typename
						... on B2BlockReservationSubtype {
							organization {
								id
								name
							}
							createdByUserId @catch(to: NULL) {
								id
								name
								email
							}
						}
					}
					space {
						frame
						position
						configuration
						dimensions {
							widthMm
							heightMm
							depthMm
						}
						hub {
							id
						}
					}
				}
			}
		}
		lockReservations(filter: { subtype: B2B, flow: GOODS_RECEPTION, active: true }, pagination: { page: 0, pageSize: 1000 })
			@skip(if: $skipOwnedReservations) {
			nodes {
				id
				startedAt
				deliveredAt
				deactivatedAt
				contentNote
				note
				flow
				subtype {
					__typename
					... on B2BlockReservationSubtype {
						organization {
							id
							name
						}
						createdByUserId @catch(to: NULL) {
							id
							name
							email
						}
					}
				}
				space {
					frame
					position
					configuration
					dimensions {
						widthMm
						heightMm
						depthMm
					}
					hub {
						id
					}
				}
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/warenannahme/pickup")({
	beforeLoad: () => {
		document.title = "bacehub | Warenannahme";
	},
	component: RouteComponent,
	validateSearch: zodValidator(z.object({ reservationId: z.string().optional() })),
	loader: async (ctx) => {
		if (!(await checkSession())) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/warenannahme/pickup` },
			});
		}
		return preloadQuery<pickupWarenannahmeQuery>(ctx.context.relayEnvironment, query, {
			hubName: ctx.params.hubMetaName,
			skipOwnedReservations: !getUserOrganizationId(),
		});
	},
});

type Reservation = NonNullable<pickupWarenannahmeQuery["response"]["lockReservations"]>["nodes"][number];
type Item = WarenannahmeItem<Reservation>;
/** The driver and the organization that delivered (both only on rentals of organizations). */
const delivery = (reservation: Reservation) => {
	const subtype = reservation.subtype.__typename === "B2BlockReservationSubtype" ? reservation.subtype : null;
	return {
		by: subtype?.createdByUserId?.name || subtype?.createdByUserId?.email,
		organization: subtype?.organization.name,
	};
};
type Step = { type: "select" } | { type: "open"; item: Item } | { type: "summary"; item: Item; collectedAt: string };

function RouteComponent() {
	const navigate = useNavigate();
	const i18n = useI18nContext().LL["new-ui"];
	const data = usePreloadedQuery<pickupWarenannahmeQuery>(query, Route.useLoaderData());
	const { hubMeta } = data;
	const [step, setStep] = useState<Step>({ type: "select" });
	const toOverview = () => navigate({ to: "/$hubMetaName/warenannahme", params: { hubMetaName: hubMeta.name } });

	const { reservationId } = Route.useSearch();
	// The hub owner collects the goods. The delivering organization only takes them out again
	// (a mistake, or they don't fit), which removes the delivery: it confirms that first.
	const removesDelivery = getUserOrganizationId() !== hubMeta.organization.id;
	const [confirmRemove, setConfirmRemove] = useState<Item | null>(null);
	const [all] = useState(() =>
		warenannahmeItems<Reservation>({
			hubId: hubMeta.hub.id,
			owned: data.lockReservations?.nodes ?? [],
			grants: data.myUtilizationGrants.nodes,
			includeEnded: false,
			organizationId: getUserOrganizationId(),
			hubOwnerId: hubMeta.organization.id,
		}).filter((item) => item.canOpen)
	);
	// Opened from a compartment card of the overview: that compartment is already chosen.
	const preselected = all.find((item) => item.reservationId === reservationId);
	const items = preselected ? [preselected] : all;
	const hubGate = useHubGate(
		{
			id: hubMeta.hub.id,
			name: hubMeta.name,
			lat: hubMeta.lat,
			lng: hubMeta.lng,
			frames: hubMeta.hub.spaces.map((space) => space.frame),
		},
		{ onCancel: preselected ? toOverview : undefined }
	);
	const startedPreselected = useRef(false);
	useEffect(() => {
		if (!preselected || startedPreselected.current || hubMeta.hub.status !== "ONLINE") return;
		startedPreselected.current = true;
		if (removesDelivery) setConfirmRemove(preselected);
		else hubGate.run(() => setStep({ type: "open", item: preselected }));
	}, [preselected, hubGate, hubMeta.hub.status, removesDelivery]);
	const open = (item: Item) => hubGate.run(() => setStep({ type: "open", item }));
	const start = (item: Item) => (removesDelivery ? setConfirmRemove(item) : open(item));
	const removeDialog = (
		<AlertDialog
			open={confirmRemove !== null}
			onOpenChange={(isOpen) => {
				if (isOpen) return;
				setConfirmRemove(null);
				if (preselected) toOverview();
			}}
		>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{i18n.WA_REMOVE_CONFIRM_TITLE()}</AlertDialogTitle>
					<AlertDialogDescription>{i18n.WA_REMOVE_CONFIRM_DESC()}</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel>{i18n.WA_REMOVE_CANCEL()}</AlertDialogCancel>
					<AlertDialogAction
						onClick={(event) => {
							event.preventDefault();
							const item = confirmRemove;
							setConfirmRemove(null);
							if (item) open(item);
						}}
					>
						{i18n.WA_REMOVE_CONFIRM()}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
	const counts = new Map<SizeBucket, number>();
	for (const item of items) {
		const size = getSizeBucket(item.configuration);
		if (size) counts.set(size, (counts.get(size) ?? 0) + 1);
	}
	const [sizeFilter, setSizeFilter] = useState<SizeBucket | undefined>(() => [...counts.keys()][0]);
	const visibleItems = sizeFilter ? items.filter((item) => getSizeBucket(item.configuration) === sizeFilter) : items;

	if (step.type === "open") {
		const { item } = step;
		return (
			<WarenannahmeDoorFlow
				hubId={hubMeta.hub.id}
				hubName={hubMeta.name}
				side={compartmentSide(
					hubMeta.hub.spaces.map((space) => space.frame),
					item.reservation.space.frame
				)}
				mode="collect"
				access={
					item.grantId ? { type: "grant", grantId: item.grantId } : { type: "owner", reservationId: item.reservationId, endRental: true }
				}
				onDone={() => setStep({ type: "summary", item, collectedAt: new Date().toISOString() })}
			/>
		);
	}

	if (step.type === "summary") {
		const { item } = step;
		return (
			<CollectedSummary
				hubName={hubMeta.name}
				hubAddress={`${hubMeta.street}, ${hubMeta.postalCode} ${hubMeta.city}`}
				size={getSizeBucket(item.configuration) ?? ""}
				compartmentNumber={item.compartmentNumber}
				content={item.content}
				comment={item.comment}
				collectedAt={step.collectedAt}
				actionLabel={i18n.WA_BACK_TO_OVERVIEW()}
				onAction={() =>
					navigate({
						to: "/$hubMetaName/warenannahme",
						params: { hubMetaName: hubMeta.name },
						search: { done: removesDelivery ? "removed" : "collected" },
					})
				}
			/>
		);
	}

	const isHubOnline = hubMeta.hub.status === "ONLINE";

	if (preselected) {
		return (
			<PageContainer>
				<AppHeader hubMetaName={hubMeta.name} service="LOCK_B2B" />
				{!isHubOnline ? (
					<div className="flex flex-col gap-6 px-6 pt-6">
						<p role="status" className="rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground">
							<T>{i18n.WA_HUB_OFFLINE()}</T>
						</p>
						<Button variant="outline" className="w-full" onClick={toOverview}>
							<ArrowLeft className="size-4" />
							<T>{i18n.WA_BACK_TO_OVERVIEW()}</T>
						</Button>
					</div>
				) : null}
				{hubGate.drawer}
				{removeDialog}
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<AppHeader hubMetaName={hubMeta.name} service="LOCK_B2B" />
			<div className="flex flex-col gap-6 px-6 pt-6">
				<h1 className="text-2xl font-breit font-medium">
					<T>{i18n.WA_SELECT_TITLE()}</T>
				</h1>
				{!isHubOnline ? (
					<p role="status" className="rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground">
						<T>{i18n.WA_HUB_OFFLINE()}</T>
					</p>
				) : null}

				{items.length === 0 ? (
					<>
						<p className="text-sm text-muted-foreground">
							<T>{i18n.WA_SELECT_EMPTY_COLLECT()}</T>
						</p>
						<Button variant="outline" className="w-full" onClick={toOverview}>
							<ArrowLeft className="size-4" />
							<T>{i18n.WA_BACK_TO_OVERVIEW()}</T>
						</Button>
					</>
				) : (
					<>
						<SizeTabs counts={counts} value={sizeFilter} onChange={setSizeFilter} />
						<div className="flex flex-col gap-4">
							{visibleItems.map((item) => (
								<div
									key={item.key}
									className={`flex flex-col gap-4 rounded-[14px] border bg-background p-6 shadow-md ${isHubOnline ? "" : "opacity-50"}`}
								>
									<div className="flex min-w-0 flex-col gap-1">
										<p className="text-xl font-breit font-medium leading-none">{formatConfigurationLabel(item.configuration)}</p>
										<p className="text-base text-muted-foreground">{formatDimensions(item.reservation.space.dimensions)}</p>
									</div>
									<div className="flex flex-col gap-3">
										<div className="flex items-start justify-between gap-4">
											<span className="text-sm text-[#737373]">
												<T>{i18n.WA_CARD_COMPARTMENT()}</T>
											</span>
											<span className="text-sm font-medium">{i18n.WA_COMPARTMENT_NUMBER({ number: item.compartmentNumber })}</span>
										</div>
										{removesDelivery ? null : (
											<>
												<div className="flex items-start justify-between gap-4">
													<span className="text-sm text-[#737373]">
														<T>{i18n.WA_CARD_DELIVERED_BY()}</T>
													</span>
													<span className="text-right text-sm font-medium break-words">{delivery(item.reservation).by || "–"}</span>
												</div>
												<div className="flex items-start justify-between gap-4">
													<span className="text-sm text-[#737373]">
														<T>{i18n.WA_CARD_ORGANIZATION()}</T>
													</span>
													<span className="text-right text-sm font-medium break-words">
														{delivery(item.reservation).organization || "–"}
													</span>
												</div>
											</>
										)}
										{item.content ? (
											<div className="flex items-start justify-between gap-4">
												<span className="text-sm text-[#737373]">
													<T>{i18n.WA_CARD_CONTENT()}</T>
												</span>
												<span className="text-right text-sm font-medium break-words">{item.content}</span>
											</div>
										) : null}
										<div className="flex items-start justify-between gap-4">
											<span className="text-sm text-[#737373]">
												<T>{i18n.WA_CARD_COMMENT()}</T>
											</span>
											<span className="text-right text-sm font-medium break-words">{item.comment || "–"}</span>
										</div>
									</div>
									<Button className="w-full" disabled={!isHubOnline} onClick={() => start(item)}>
										<T>{removesDelivery ? i18n.WA_CARD_REMOVE_CTA() : i18n.WA_COLLECT_CTA()}</T>
									</Button>
								</div>
							))}
						</div>
					</>
				)}
			</div>
			{hubGate.drawer}
			{removeDialog}
		</PageContainer>
	);
}
